import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { CurrentUserService } from './current-user.service';
import { LoginService } from 'src/app/modules/users/services/login.service';

/**
 * HTTP Interceptor for handling token refresh on 401 errors
 * SECURITY FIX: VAPT Issue #3 - Iteration 1
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );

  constructor(
    private injector: Injector,
    private currentUserService: CurrentUserService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(req, next);
        }

        return throwError(() => error);
      })
    );
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Don't try to refresh token for login/refresh endpoints
    if (
      request.url.includes('/Login') ||
      request.url.includes('/RefreshToken')
    ) {
      // Skip server logout call - tokens are already invalid (401 from RefreshToken)
      this.currentUserService.logOut(true, false, true);
      return throwError(() => new Error('Authentication failed'));
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.currentUserService.getRefreshToken();

      if (!refreshToken) {
        this.isRefreshing = false;
        console.warn(
          'Session expired: No refresh token available. User will be redirected to login.'
        );
        // Clear local data without making API call since we have no token to revoke
        this.currentUserService.logOut(false);
        return throwError(
          () => new Error('Session expired - please login again')
        );
      }

      // Use injector to get LoginService to avoid circular dependency
      const loginService = this.injector.get(LoginService);

      return loginService.refreshToken(refreshToken).pipe(
        switchMap((tokens: { accessToken: string; refreshToken: string }) => {
          this.isRefreshing = false;

          // Update tokens in storage
          this.currentUserService.updateTokens(
            tokens.accessToken,
            tokens.refreshToken
          );
          this.refreshTokenSubject.next(tokens.accessToken);

          // Retry the original request with the new token
          return next.handle(this.addToken(request, tokens.accessToken));
        }),
        catchError(err => {
          this.isRefreshing = false;

          // Refresh token failed, logout user
          // Skip server logout call - if refresh failed, access token is also invalid
          console.error('Token refresh failed:', err);
          this.currentUserService.logOut(true, false, true);
          return throwError(() => err);
        })
      );
    } else {
      // Wait for token refresh to complete, then retry with new token
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          return next.handle(this.addToken(request, token));
        })
      );
    }
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
